/**
 * Report form (tutoring).
 */
import * as React from 'react';
import { BookOpen, Star, FileText, Users } from 'lucide-react';
import { Rating } from '@/components/forms/Rating';
import { Textarea } from '@/components/forms/Textarea';
import { MultiSelect } from '@/components/forms/MultiSelect';
import {
  ACTIVITES_REALISEES_SELECT_OPTIONS,
  TUTORING_REPORT_EVALUATION_FIELDS,
  TUTORING_REPORT_SKILLS_FIELDS,
  REMOTE_SUBJECT_OPTIONS,
} from '@/components/reports/report-configs';
import { setReportField } from '@/components/reports/remote/report-form-utils';

/** Props for {@link TutoringReportForm}. */
export interface TutoringReportFormProps {
  fields: Record<string, unknown>;
  onChange: (patch: Record<string, unknown>) => void;
  isReadOnly: boolean;
}

/** Remote tutoring report fields. */
export const TutoringReportForm: React.FC<Readonly<TutoringReportFormProps>> = ({
  fields,
  onChange,
  isReadOnly,
}) => {
  const set = (key: string, value: unknown) => setReportField(onChange, key, value);
  const subjectOptions = [...REMOTE_SUBJECT_OPTIONS];

  return (
    <>
      <div className="form-section">
        <h4 className="form-section-title">
          <Star className="w-4 h-4" />
          Évaluation de la séance
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Rating
            label="Note globale"
            required
            value={(fields.noteGlobale as number) ?? null}
            onChange={(v) => set('noteGlobale', v)}
            helperText="Note principale de la séance /5"
            readOnly={isReadOnly}
          />
          {TUTORING_REPORT_EVALUATION_FIELDS.map(({ key, label }) => (
            <Rating
              key={key}
              label={label}
              required
              value={(fields[key] as number) ?? null}
              onChange={(v) => set(key, v)}
              readOnly={isReadOnly}
            />
          ))}
        </div>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <Users className="w-4 h-4" />
          Compétences de l'élève
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {TUTORING_REPORT_SKILLS_FIELDS.map(({ key, label }) => (
            <Rating
              key={key}
              label={label}
              required
              value={(fields[key] as number) ?? null}
              onChange={(v) => set(key, v)}
              readOnly={isReadOnly}
            />
          ))}
        </div>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <BookOpen className="w-4 h-4" />
          Contenu de la séance
        </h4>
        <div className="space-y-4">
          <MultiSelect
            label="Activités réalisées"
            required
            placeholder="Sélectionner les activités"
            options={ACTIVITES_REALISEES_SELECT_OPTIONS}
            value={(fields.activitesRealisees as string[]) ?? []}
            onChange={(v) => set('activitesRealisees', v)}
            helperText="Activités réalisées durant la séance"
            disabled={isReadOnly}
          />
          <MultiSelect
            label="Matière principale"
            required
            placeholder="Sélectionner une matière"
            options={subjectOptions}
            value={fields.matierePrincipale ? [fields.matierePrincipale as string] : []}
            onChange={(v) => set('matierePrincipale', v.at(-1) ?? null)}
            helperText="Matière principale abordée"
            maxVisibleTags={1}
            disabled={isReadOnly}
          />
          <Textarea
            label="Compétences transversales"
            placeholder="Ex: Méthode de travail, gestion du temps…"
            value={(fields.competencesTransversales as string) ?? ''}
            onChange={(e) => set('competencesTransversales', e.target.value)}
            rows={2}
            autoResize
            disabled={isReadOnly}
          />
        </div>
      </div>

      <div className="form-section">
        <h4 className="form-section-title">
          <FileText className="w-4 h-4" />
          Observations et messages
        </h4>
        <div className="space-y-4">
          <Textarea
            label="Résumé de la séance"
            required
            placeholder="Décrivez le déroulement de la séance…"
            value={(fields.resumeSeance as string) ?? ''}
            onChange={(e) => set('resumeSeance', e.target.value)}
            rows={3}
            autoResize
            maxLength={2000}
            showCharCount
            disabled={isReadOnly}
          />
          <Textarea
            label="Observation coordinateur"
            placeholder="Observation confidentielle pour le coordinateur…"
            value={(fields.observationCoordinateur as string) ?? ''}
            onChange={(e) => set('observationCoordinateur', e.target.value)}
            rows={2}
            autoResize
            helperText="Confidentiel — visible par le coordinateur uniquement"
            disabled={isReadOnly}
          />
          <Textarea
            label="Conseil à l'élève"
            placeholder="Conseil pédagogique pour l'élève…"
            value={(fields.conseilEleve as string) ?? ''}
            onChange={(e) => set('conseilEleve', e.target.value)}
            rows={2}
            autoResize
            disabled={isReadOnly}
          />
          <Textarea
            label="Message à l'élève"
            placeholder="Message direct à l'élève…"
            value={(fields.messageEleve as string) ?? ''}
            onChange={(e) => set('messageEleve', e.target.value)}
            rows={2}
            autoResize
            disabled={isReadOnly}
          />
          <Textarea
            label="Message au parent"
            placeholder="Message à destination du parent…"
            value={(fields.messageParent as string) ?? ''}
            onChange={(e) => set('messageParent', e.target.value)}
            rows={2}
            autoResize
            disabled={isReadOnly}
          />
        </div>
      </div>
    </>
  );
};
